/**
 * Wording and counting helpers for the fleet Publish and Publish to Device flows
 * (KAN-520 fleet publish skips standalone devices, KAN-515 standalone Publish to Device result). Pure functions, no Angular imports.
 * All user-facing strings of these flows live here (PO-confirmed wording).
 */

export interface PublishTargetCandidate { overrideGlobalLayout?: boolean }

/** Tooltip + accessible label for the standalone badge in the fleet grid (KAN-520). */
export const STANDALONE_NOT_IN_FLEET_PUBLISH = 'Not included in fleet publish';

/** Error wording for a failed standalone Publish to Device (KAN-515). */
export const STANDALONE_PUBLISH_FAILED = 'Publish failed. This machine was not changed.';

export interface FleetPublishCounts { publishTo: number; skippedStandalone: number }

/** Machines a fleet publish will reach (inheriting) and the standalone machines it skips. */
export function countFleetPublishTargets(booths: PublishTargetCandidate[]): FleetPublishCounts {
  const skippedStandalone = booths.filter(b => b?.overrideGlobalLayout === true).length;
  return { publishTo: booths.length - skippedStandalone, skippedStandalone };
}

/** KAN-520 confirmation text. */
export function fleetPublishConfirmMessage(counts: FleetPublishCounts): string {
  if (counts.skippedStandalone <= 0) return `Publish to ${counts.publishTo} machines?`;
  return `Publish to ${counts.publishTo} machines? ${counts.skippedStandalone} standalone machines will be skipped. Publish those with Publish to Device.`;
}

/** Sorted, de-duplicated, non-empty string ids from the version doc's `skippedStandaloneDevices` (tolerates a missing or malformed field). */
export function skippedIdsFromVersionDoc(versionDoc: any): string[] {
  const raw = versionDoc?.skippedStandaloneDevices;
  if (!Array.isArray(raw)) return [];
  return [...new Set(raw.filter((x: any) => typeof x === 'string' && x.trim() !== ''))].sort() as string[];
}

/** KAN-520 result text; the second sentence is omitted when nothing was skipped. Count comes from the doc when present, else from the id list. */
export function fleetPublishResultMessage(publishedCount: number, versionDoc: any): string {
  const ids = skippedIdsFromVersionDoc(versionDoc);
  const docCount = versionDoc?.skippedStandaloneCount;
  const skipped = typeof docCount === 'number' && docCount >= 0 ? docCount : ids.length;
  const first = `Published to ${publishedCount} machines.`;
  if (skipped <= 0) return first;
  return ids.length > 0 ? `${first} Skipped ${skipped} standalone machines: ${ids.join(', ')}.` : `${first} Skipped ${skipped} standalone machines.`;
}

/** KAN-515: the text shown after Publish to Device. Uses the request doc's `result.message`; null when the function wrote none. */
export function devicePublishResultMessage(requestDoc: any): string | null {
  const m = requestDoc?.result?.message;
  return typeof m === 'string' && m.trim() !== '' ? m : null;
}

/** KAN-515: error text after a failed Publish to Device. Standalone machines get the confirmed wording; the function's own error (if any) follows. */
export function devicePublishErrorMessage(detail: string | null | undefined, isStandalone: boolean): string {
  const d = (detail || '').trim();
  if (isStandalone) return d && d !== 'Publish failed' ? `${STANDALONE_PUBLISH_FAILED} ${d}` : STANDALONE_PUBLISH_FAILED;
  return 'Failed to publish to device. See console for details.';
}
